import { isSepaCountry } from "helpers/sepaZone";
import type { BankingStatusRule } from "types/countryFeatures";

type BankingStatus = "local" | "foreign_local" | "foreign_international";

function matchesCondition(
    condition: BankingStatusRule["condition"],
    bankCountry: string,
    companyCountry: string,
): boolean {
    switch (condition.type) {
        case "sameCountry":
            return bankCountry.toUpperCase() === companyCountry.toUpperCase();
        case "zone":
            if (condition.zone === "SEPA") return isSepaCountry(bankCountry);
            return false;
        case "countries":
            return condition.codes.map((c) => c.toUpperCase()).includes(bankCountry.toUpperCase());
        case "default":
            return true;
    }
}

/**
 * Evaluates bankingStatusRules top-to-bottom and returns the status for the first matching rule.
 * Falls back to "local" if no rule matches (should not happen when rules include a "default").
 */
export function resolveBankingStatus(
    rules: BankingStatusRule[],
    bankCountry: string,
    companyCountry: string,
): BankingStatus {
    for (const rule of rules) {
        if (matchesCondition(rule.condition, bankCountry, companyCountry)) {
            return rule.status;
        }
    }
    return "local";
}

/**
 * Derives "local" | "international" display mode from a bank country + company country.
 * Uses rules when available (FR/BE), falls back to direct country comparison (CA/UK).
 */
export function deriveBankingMode(
    bankCountry: string,
    companyIsoCode: string,
    rules: BankingStatusRule[] | undefined,
): "local" | "international" {
    if (rules?.length) {
        return resolveBankingStatus(rules, bankCountry, companyIsoCode) !== "local" ? "international" : "local";
    }
    return bankCountry.toUpperCase() !== companyIsoCode.toUpperCase() ? "international" : "local";
}
